import React from 'react';
import styled, { css } from 'styled-components';
import { Theme, type SystemSize } from '~/theme';
import Checkbox from '~/components/molecule/Checkbox';
import MasonryCard from '~/components/molecule/MasonryCard';
import RadioButton from '../RadioButton';
import { Body, Label } from '~/components/atom/Typography';
import isPrimitive from '~/util/isPrimitive';
import arrayToCss from '~/util/arrayToCss';

export type Props = {
  checkboxTitle: React.ReactNode;
  description?: React.ReactNode;
  type: 'checkbox' | 'radio';
  maxCardWidth?: string;
  checked: boolean;
  disabled?: boolean;
  margin?: Array<SystemSize | null>;
  padding?: Array<SystemSize | null>;
  onClick?: () => void;
  /** When true, only the checkbox/radio input toggles — clicking the card body does nothing. */
  checkboxOnly?: boolean;
};

const SelectBlock: React.FCC<Props> = ({
  dataTestId,
  type,
  checkboxTitle,
  checked,
  disabled,
  description,
  children,
  maxCardWidth,
  margin,
  padding = ['l'],
  onClick,
  checkboxOnly,
  ...rest
}) => {
  const componentByType = {
    checkbox: StyledCheckbox,
    radio: StyledRadioButton,
  };

  const ComponentByType: React.FCC<CardProps> =
    componentByType[type] ?? componentByType.checkbox;

  const onBlockClick = disabled ? () => {} : onClick;

  const label = isPrimitive(checkboxTitle) ? (
    <Label
      margin={[type === 'checkbox' ? 'xxxs' : null, null, null, 'xxxs']}
      color={{ group: 'secondary' }}
      size="base"
    >
      {checkboxTitle}
    </Label>
  ) : (
    checkboxTitle
  );

  return (
    <StyledMasonryCard
      {...rest}
      dataTestId={dataTestId}
      $checked={checked}
      disabled={disabled}
      onClick={checkboxOnly ? undefined : onBlockClick}
      maxWidth={maxCardWidth}
      $margin={margin}
      $padding={padding}
      $checkboxOnly={checkboxOnly}
    >
      <ComponentByType
        value={checked}
        label={label}
        checked={checked}
        disabled={disabled}
        onChange={onBlockClick}
        dataTestId={`${type}-test-id`}
      />
      {description && (
        <Body margin={['xxs', null, null, 'xl']} size="base">
          {description}
        </Body>
      )}
      {children}
    </StyledMasonryCard>
  );
};

type CardProps = {
  value?: boolean;
  label?: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onClick?: Props['onClick'];
  onChange?: Props['onClick'];
};

const getComponentColor = (theme: Theme, disabled: boolean = false): string =>
  disabled ? theme.color('tertiary', 'dark') : theme.color('secondary');

const StyledMasonryCard = styled(MasonryCard)<{
  $checked?: boolean;
  disabled?: boolean;
  $margin?: Array<SystemSize | null>;
  $padding?: Array<SystemSize | null>;
  $checkboxOnly?: boolean;
}>(
  ({
    theme,
    $checked,
    disabled,
    $margin = [null, null, 'l', null],
    $padding = ['l'],
    $checkboxOnly,
  }) => css`
    user-select: none;

    margin-bottom: ${arrayToCss($margin, theme)};
    padding: ${arrayToCss($padding, theme)};
    min-height: unset;

    border: ${theme.getTokens().border.width.s} solid
      ${$checked
        ? getComponentColor(theme, disabled)
        : theme.color('primary', 'translucent')};
    transition: all 0.3s ease;
    cursor: ${disabled ? 'not-allowed' : $checkboxOnly ? 'default' : 'pointer'};

    ${!disabled &&
    css`
      &:hover {
        border: ${theme.getTokens().border.width.s} solid
          ${theme.color('secondary')};
      }
    `}
  `,
);

const StyledRadioButton = styled(RadioButton)<CardProps>(
  () => css`
    cursor: inherit;
  `,
);

const StyledCheckbox = styled(Checkbox)<CardProps>(
  ({ theme, disabled }) => css`
    cursor: inherit;
    font-weight: ${theme.fw('semiBold')};
    color: ${disabled
      ? getComponentColor(theme, disabled)
      : theme.color('secondary')};

    & > input {
      &:hover,
      &:focus {
        border-color: ${theme.color('tertiary', 'dark')};
      }
    }
  `,
);

export default SelectBlock;
